import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../auth';
import { formatShortDateTime } from '../../../shared/dates';
import type {
  CRMDiscoveryPlatform,
  DiscoveryTargetSegment,
  LeadDiscoveryCampaign,
  LeadDiscoveryCandidate,
  LeadDiscoveryCandidateStatus,
  LeadDiscoveryRun,
} from '@modelcast/types';

const PLATFORM_OPTIONS: { value: CRMDiscoveryPlatform; label: string }[] = [
  { value: 'google', label: 'Web' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'eventbrite', label: 'Eventbrite' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'art_school', label: 'Schools' },
  { value: 'facebook', label: 'Facebook' },
];

const STATUS_OPTIONS: { value: LeadDiscoveryCandidateStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'new', label: 'New' },
  { value: 'approved', label: 'Approved' },
  { value: 'imported', label: 'Imported' },
  { value: 'duplicate', label: 'Duplicates' },
  { value: 'rejected', label: 'Rejected' },
];

interface CampaignForm {
  name: string;
  targetSegment: DiscoveryTargetSegment;
  region: string;
  language: string;
  goal: string;
  searchTerms: string;
  excludedTerms: string;
  sourcePlatforms: CRMDiscoveryPlatform[];
  qualityThreshold: number;
  runIntervalHours: string;
}

const defaultForm: CampaignForm = {
  name: 'Global English online life drawing organizers',
  targetSegment: 'organizer',
  region: 'global online',
  language: 'English',
  goal: 'Find recurring online life drawing hosts and organizers who may want to run DrawClub events.',
  searchTerms: [
    'online life drawing organizer',
    'virtual life drawing class',
    'zoom life drawing session',
    'online figure drawing workshop',
    'online long pose drawing',
    'life drawing studio online',
  ].join('\n'),
  excludedTerms: ['fashion model', 'kids class', 'anime figure', 'stock photo'].join('\n'),
  sourcePlatforms: ['google', 'instagram', 'eventbrite', 'meetup', 'art_school'],
  qualityThreshold: 75,
  runIntervalHours: '168',
};

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
}

function scoreClass(score: number): string {
  if (score >= 75) return 'border-ok/25 bg-ok/10 text-ok';
  if (score >= 55) return 'border-warn/25 bg-warn/10 text-warn';
  return 'border-line bg-panel text-text-dim';
}

function statusClass(status: LeadDiscoveryCandidateStatus): string {
  switch (status) {
    case 'reviewing':
    case 'approved':
      return 'border-ok/25 bg-ok/10 text-ok';
    case 'duplicate':
      return 'border-warn/25 bg-warn/10 text-warn';
    case 'rejected':
      return 'border-red-500/20 bg-red-500/10 text-red-600';
    case 'imported':
      return 'border-accent/25 bg-accent/10 text-accent';
    case 'new':
      return 'border-line bg-panel text-text-dim';
    default:
      return 'border-line bg-panel text-text-dim';
  }
}

export function LeadGenerationPage() {
  const { getToken } = useAuth();
  const [campaigns, setCampaigns] = useState<LeadDiscoveryCampaign[]>([]);
  const [runs, setRuns] = useState<LeadDiscoveryRun[]>([]);
  const [candidates, setCandidates] = useState<LeadDiscoveryCandidate[]>([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<LeadDiscoveryCandidateStatus | ''>('');
  const [minScore, setMinScore] = useState(60);
  const [form, setForm] = useState<CampaignForm>(defaultForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const selectedCampaign = useMemo(
    () => campaigns.find(campaign => campaign.id === selectedCampaignId) ?? null,
    [campaigns, selectedCampaignId]
  );
  const latestRun = runs[0] ?? null;
  // Intelligence-first ordering: outreach priority before raw score.
  const topCandidates = useMemo(
    () =>
      [...candidates]
        .sort(
          (a, b) =>
            (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3) ||
            b.qualityScore - a.qualityScore
        )
        .slice(0, 50),
    [candidates]
  );

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    fetchRuns();
    fetchCandidates();
  }, [selectedCampaignId, statusFilter, minScore]);

  async function apiFetch(path: string, init: RequestInit = {}) {
    const token = await getToken();
    return fetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...init.headers,
      },
    });
  }

  async function loadInitialData() {
    setIsLoading(true);
    try {
      const response = await apiFetch('/api/admin/crm/discovery/campaigns');
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns);
        if (data.campaigns[0]) setSelectedCampaignId(data.campaigns[0].id);
      }
    } catch (error) {
      console.error('Error loading discovery campaigns:', error);
      setStatus('Could not load discovery campaigns.');
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchRuns() {
    try {
      const params = new URLSearchParams();
      if (selectedCampaignId) params.set('campaignId', selectedCampaignId);
      const response = await apiFetch(`/api/admin/crm/discovery/runs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs);
      }
    } catch (error) {
      console.error('Error loading discovery runs:', error);
    }
  }

  async function fetchCandidates() {
    try {
      const params = new URLSearchParams({
        minScore: String(minScore),
        limit: '80',
      });
      if (selectedCampaignId) params.set('campaignId', selectedCampaignId);
      if (statusFilter) params.set('status', statusFilter);
      const response = await apiFetch(`/api/admin/crm/discovery/candidates?${params}`);
      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates);
        setTotalCandidates(data.total);
      }
    } catch (error) {
      console.error('Error loading discovery candidates:', error);
    }
  }

  async function createCampaign(event: FormEvent) {
    event.preventDefault();
    setIsCreating(true);
    setStatus(null);
    try {
      const response = await apiFetch('/api/admin/crm/discovery/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          targetSegment: form.targetSegment,
          region: form.region,
          language: form.language,
          goal: form.goal,
          searchTerms: splitLines(form.searchTerms),
          excludedTerms: splitLines(form.excludedTerms),
          sourcePlatforms: form.sourcePlatforms,
          qualityThreshold: form.qualityThreshold,
          runIntervalHours: form.runIntervalHours ? parseInt(form.runIntervalHours, 10) : null,
        }),
      });
      if (!response.ok) throw new Error('Failed to create campaign');
      const data = await response.json();
      setCampaigns(current => [data.campaign, ...current]);
      setSelectedCampaignId(data.campaign.id);
      setStatus('Campaign created.');
    } catch (error) {
      console.error('Error creating discovery campaign:', error);
      setStatus('Could not create the campaign.');
    } finally {
      setIsCreating(false);
    }
  }

  async function startRun() {
    setIsRunning(true);
    setStatus(null);
    try {
      const response = await apiFetch('/api/admin/crm/discovery/runs', {
        method: 'POST',
        body: JSON.stringify(selectedCampaignId ? { campaignId: selectedCampaignId, targetCount: 50 } : {
          targetSegment: form.targetSegment,
          region: form.region,
          language: form.language,
          goal: form.goal,
          searchTerms: splitLines(form.searchTerms),
          excludedTerms: splitLines(form.excludedTerms),
          sourcePlatforms: form.sourcePlatforms,
          targetCount: 50,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to run discovery');
      setRuns(current => [data.run, ...current]);
      setCandidates(data.candidates);
      setTotalCandidates(data.candidates.length);
      setStatus(data.providerStatus);
      await loadInitialData();
    } catch (error) {
      console.error('Error starting discovery run:', error);
      setStatus(error instanceof Error ? error.message : 'Discovery run failed.');
    } finally {
      setIsRunning(false);
    }
  }

  async function updateCandidate(id: string, updates: Partial<LeadDiscoveryCandidate>) {
    try {
      const response = await apiFetch(`/api/admin/crm/discovery/candidates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update candidate');
      const data = await response.json();
      setCandidates(current => current.map(candidate => candidate.id === id ? data.candidate : candidate));
    } catch (error) {
      console.error('Error updating candidate:', error);
      setStatus('Could not update the candidate.');
    }
  }

  async function importCandidate(id: string) {
    try {
      const response = await apiFetch(`/api/admin/crm/discovery/candidates/${id}/import`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import candidate');
      setCandidates(current => current.map(candidate => candidate.id === id ? data.candidate : candidate));
      setStatus('Imported lead into CRM.');
    } catch (error) {
      console.error('Error importing candidate:', error);
      setStatus(error instanceof Error ? error.message : 'Could not import the lead.');
    }
  }

  function togglePlatform(platform: CRMDiscoveryPlatform) {
    setForm(current => ({
      ...current,
      sourcePlatforms: current.sourcePlatforms.includes(platform)
        ? current.sourcePlatforms.filter(item => item !== platform)
        : [...current.sourcePlatforms, platform],
    }));
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Lead Intelligence</h1>
          <p className="text-sm text-text-dim mt-1">
            Dossier-grade leads: who they are, proof they're active, how to reach them, and the personal angle for outreach. Discovery runs feed this list; the intelligence comes first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/admin/crm" className="btn btn-sm">CRM</a>
          <button type="button" onClick={startRun} className="btn btn-accent btn-sm" disabled={isRunning}>
            {isRunning ? 'Running...' : 'Run discovery'}
          </button>
        </div>
      </div>

      {status && (
        <div className="rounded-lg border border-line bg-panel px-4 py-3 text-sm text-text-dim">
          {status}
        </div>
      )}

      <div className="grid grid-cols-1 2xl:grid-cols-[420px_minmax(0,1fr)] gap-5">
        <aside className="space-y-5">
          <section className="bg-panel border border-line rounded-lg p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-sm font-semibold text-text">Campaigns</h2>
              {isLoading && <span className="text-xs text-text-dim">Loading...</span>}
            </div>
            <div className="space-y-2">
              {campaigns.length === 0 ? (
                <div className="text-sm text-text-dim">No campaigns yet.</div>
              ) : campaigns.map(campaign => (
                <button
                  type="button"
                  key={campaign.id}
                  onClick={() => setSelectedCampaignId(campaign.id)}
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${
                    campaign.id === selectedCampaignId ? 'border-accent/40 bg-accent/10' : 'border-line bg-bg hover:border-text-faint'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-text">{campaign.name}</div>
                    <span className="text-[11px] text-text-dim capitalize">{campaign.status}</span>
                  </div>
                  <div className="text-xs text-text-dim mt-1">
                    {campaign.language} · {campaign.region || 'global'} · threshold {campaign.qualityThreshold}
                  </div>
                  {campaign.learningSummary && (
                    <div className="text-xs text-text-faint mt-2 line-clamp-3 whitespace-pre-line">
                      {campaign.learningSummary}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>

          <form onSubmit={createCampaign} className="bg-panel border border-line rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-text">New campaign</h2>
              <button type="submit" className="btn btn-sm" disabled={isCreating}>
                {isCreating ? 'Saving...' : 'Save'}
              </button>
            </div>
            <div className="space-y-3">
              <Input label="Name" value={form.name} onChange={name => setForm(current => ({ ...current, name }))} />
              <Input label="Region" value={form.region} onChange={region => setForm(current => ({ ...current, region }))} />
              <Input label="Language" value={form.language} onChange={language => setForm(current => ({ ...current, language }))} />
              <Textarea label="Goal" value={form.goal} onChange={goal => setForm(current => ({ ...current, goal }))} rows={3} />
              <Textarea label="Search terms" value={form.searchTerms} onChange={searchTerms => setForm(current => ({ ...current, searchTerms }))} rows={7} />
              <Textarea label="Excluded terms" value={form.excludedTerms} onChange={excludedTerms => setForm(current => ({ ...current, excludedTerms }))} rows={4} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Threshold" type="number" value={String(form.qualityThreshold)} onChange={qualityThreshold => setForm(current => ({ ...current, qualityThreshold: parseInt(qualityThreshold, 10) || 75 }))} />
                <Input label="Run every hours" type="number" value={form.runIntervalHours} onChange={runIntervalHours => setForm(current => ({ ...current, runIntervalHours }))} />
              </div>
              <div>
                <div className="text-[11px] text-text-dim mb-2">Sources</div>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => togglePlatform(option.value)}
                      className={`px-2.5 py-1.5 rounded border text-xs ${
                        form.sourcePlatforms.includes(option.value)
                          ? 'border-accent/40 bg-accent/10 text-accent'
                          : 'border-line bg-bg text-text-dim'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </aside>

        <main className="space-y-5 min-w-0">
          <section className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <Metric label="Campaign" value={selectedCampaign ? selectedCampaign.name : 'Ad hoc'} />
            <Metric label="Latest run" value={latestRun ? formatShortDateTime(latestRun.completedAt ?? latestRun.createdAt) : '-'} />
            <Metric label="Candidates" value={String(totalCandidates)} />
            <Metric label="Average score" value={latestRun?.averageScore ? String(latestRun.averageScore) : '-'} />
          </section>

          {latestRun && (
            <section className="bg-panel border border-line rounded-lg p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-text">Latest evaluation</h2>
                  <p className="text-sm text-text-dim mt-1 max-w-4xl">{latestRun.findings.summary}</p>
                </div>
                <span className="px-2 py-1 rounded border border-line bg-bg text-[11px] text-text-dim capitalize">
                  {latestRun.status}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
                <Checklist title="Best signals" items={latestRun.findings.bestSignals} />
                <Checklist title="Quality gaps" items={latestRun.findings.weakSignals} />
                <Checklist title="Next search terms" items={latestRun.findings.recommendedSearchTerms} />
              </div>
            </section>
          )}

          <section className="bg-panel border border-line rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-sm font-semibold text-text">Organizer leads</h2>
                <p className="text-sm text-text-dim mt-1">{topCandidates.length} visible</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={event => setStatusFilter(event.target.value as LeadDiscoveryCandidateStatus | '')}
                  className="input h-9 rounded-lg text-sm"
                >
                  {STATUS_OPTIONS.map(option => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
                </select>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={minScore}
                  onChange={event => setMinScore(parseInt(event.target.value, 10) || 0)}
                  className="input h-9 w-20 rounded-lg text-sm"
                />
              </div>
            </div>

            {topCandidates.length === 0 ? (
              <div className="border border-dashed border-line rounded-lg p-8 text-center text-sm text-text-dim">
                No leads match the current filters. Import a curated research batch (Outreach/import/) or run discovery with a search provider configured on the API.
              </div>
            ) : (
              <div className="space-y-4">
                {topCandidates.map(candidate => (
                  <CandidateCard
                    key={candidate.id}
                    candidate={candidate}
                    onImport={() => importCandidate(candidate.id)}
                    onApprove={() => updateCandidate(candidate.id, { status: 'approved' })}
                    onReject={() => updateCandidate(candidate.id, { status: 'rejected' })}
                  />
                ))}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

function CandidateCard({
  candidate,
  onImport,
  onApprove,
  onReject,
}: {
  candidate: LeadDiscoveryCandidate;
  onImport: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const primaryUrl = candidate.sourceUrl || candidate.website || candidate.evidence[0]?.url;
  const sessionLink =
    candidate.evidence.find(item => item.sourcePlatform === 'booking') ??
    (candidate.artExamples[0]
      ? { url: candidate.artExamples[0].url, title: candidate.artExamples[0].title }
      : null);
  const priorityClass =
    candidate.priority === 'high'
      ? 'border-accent/40 bg-accent/10 text-accent'
      : candidate.priority === 'medium'
        ? 'border-line bg-panel text-text-dim'
        : 'border-line bg-bg text-text-faint';
  return (
    <article className="border border-line rounded-lg bg-bg p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-text">{candidate.name}</h3>
            <span className={`px-2 py-1 rounded border text-[11px] uppercase tracking-wide ${priorityClass}`}>
              {candidate.priority}
            </span>
            <span className={`px-2 py-1 rounded border text-[11px] ${scoreClass(candidate.qualityScore)}`}>
              {candidate.qualityScore}
            </span>
            <span className={`px-2 py-1 rounded border text-[11px] capitalize ${statusClass(candidate.status)}`}>
              {candidate.status}
            </span>
          </div>
          <div className="text-sm text-text-dim mt-1">
            {[candidate.organization, candidate.roleTitle, candidate.location].filter(Boolean).join(' · ')}
          </div>
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            {candidate.email && <a href={`mailto:${candidate.email}`} className="text-accent hover:underline">{candidate.email}</a>}
            {candidate.socialHandle && <a href={`https://www.instagram.com/${candidate.socialHandle.replace('@', '')}/`} target="_blank" rel="noreferrer" className="text-accent hover:underline">{candidate.socialHandle}</a>}
            {candidate.website && <a href={candidate.website} target="_blank" rel="noreferrer" className="text-accent hover:underline">Website</a>}
            {sessionLink?.url && (
              <a href={sessionLink.url} target="_blank" rel="noreferrer" className="text-accent hover:underline font-medium">
                Try a session →
              </a>
            )}
            {!sessionLink && primaryUrl && <a href={primaryUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">Evidence</a>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onReject} className="btn btn-sm">Reject</button>
          <button type="button" onClick={onApprove} className="btn btn-sm">Approve</button>
          <button type="button" onClick={onImport} className="btn btn-accent btn-sm" disabled={candidate.status === 'imported' || candidate.status === 'duplicate'}>
            Import
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.8fr)] gap-4 mt-4">
        <div className="space-y-3">
          {candidate.researchSummary && (
            <div>
              <div className="text-[10px] font-mono uppercase text-text-faint mb-1">Research brief</div>
              <p className="text-sm text-text-dim leading-relaxed">{candidate.researchSummary}</p>
            </div>
          )}
          {candidate.outreachAngle && (
            <div>
              <div className="text-[10px] font-mono uppercase text-text-faint mb-1">Personal angle</div>
              <p className="text-sm text-text-dim leading-relaxed">{candidate.outreachAngle}</p>
            </div>
          )}
          {candidate.fitReason && (
            <div>
              <div className="text-[10px] font-mono uppercase text-text-faint mb-1">Fit reason</div>
              <p className="text-sm text-text-dim leading-relaxed">{candidate.fitReason}</p>
            </div>
          )}
          {candidate.notes && (
            <div>
              <div className="text-[10px] font-mono uppercase text-text-faint mb-1">Delivery & next step</div>
              <p className="text-sm text-text-dim leading-relaxed">{candidate.notes}</p>
            </div>
          )}
        </div>
        <div className="space-y-3">
          <Checklist title="Reach & activity" items={candidate.communitySignals} />
          <Checklist title="Hooks (use on camera)" items={candidate.historySignals} />
          <Checklist title="Missing" items={candidate.missing} />
        </div>
      </div>

      {candidate.artExamples.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-mono uppercase text-text-faint mb-2">Examples and sources</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {candidate.artExamples.slice(0, 4).map(example => (
              <a
                key={example.url}
                href={example.url}
                target="_blank"
                rel="noreferrer"
                className="border border-line rounded-lg p-3 text-sm text-text-dim hover:text-text hover:border-text-faint"
              >
                <span className="block font-medium text-text truncate">{example.title || example.url}</span>
                {example.description && <span className="block text-xs text-text-faint mt-1 line-clamp-2">{example.description}</span>}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {candidate.signals.slice(0, 12).map(signal => (
          <span key={signal} className="px-2 py-1 rounded border border-line bg-panel text-[11px] text-text-dim">
            {signal}
          </span>
        ))}
        {candidate.tags.slice(0, 8).map(tag => (
          <span key={tag} className="px-2 py-1 rounded border border-accent/20 bg-accent/5 text-[11px] text-accent/80">
            {tag}
          </span>
        ))}
      </div>
    </article>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-panel border border-line rounded-lg p-4">
      <div className="text-[10px] font-mono uppercase text-text-faint">{label}</div>
      <div className="text-lg font-semibold text-text mt-1 truncate">{value}</div>
    </div>
  );
}

function Checklist({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="border border-line rounded-lg bg-bg p-3">
      <div className="text-[10px] font-mono uppercase text-text-faint mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-text-faint">None recorded</div>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 6).map(item => (
            <li key={item} className="text-xs text-text-dim leading-relaxed">{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] text-text-dim mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={event => onChange(event.target.value)}
        className="input h-10 rounded-lg text-sm"
      />
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
  rows,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] text-text-dim mb-1">{label}</span>
      <textarea
        value={value}
        onChange={event => onChange(event.target.value)}
        rows={rows}
        className="input h-auto rounded-lg text-sm py-3 leading-relaxed"
      />
    </label>
  );
}
